import {useState, useEffect, ReactNode} from 'react';
import Modal from 'react-bootstrap/Modal';
import {Link} from 'react-router-dom';
import isArray from 'lodash/isArray';
import * as Styled from './GoogleNoticeStyles';
import closeIcon from '../../../images/close-icon.svg';
import gmailIcon from '../../../images/accounts-icons/gmail-icon.svg';
import gdriveIcon from '../../../images/accounts-icons/google-drive-icon.svg';
import InsufficientIcon from '../../../images/token-insufficient-icon.png';
import RevokedIcon from '../../../images/token-revoked-icon.png';
import TeamsLimitedIcon from '../../../images/token-teams-limited-icon.png';
import React from 'react';
import googleIcon from '../../../images/googleGLogo.svg';

interface GoogleNoticeProps {
  isOpen: boolean;
  link?: string | string[];
  type: string;
  onClose: Function;
}

interface contentTypeProps{
  top: string | ReactNode;
  middle: string | ReactNode;
  headline: string | ReactNode;
  showFooter: boolean;
  connectBtns: Function;
}

const texts:any = {
  gmail: {
    top: 'If you would like to see results from your Gmail account(s) while you search, you can connect your Google account(s) to Trova. This data is only used for your search result.',
    middle: (
      <div>
        <img src={gmailIcon} alt="Gmail Logo" />
        <span>Index your emails to make them searchable for you</span>
        <ul className="token-status-container">
          <li>
            <span>Please check the box “View your email messages and settings” for Trova to search, so we won’t ask you to connect again to your account with <img src={InsufficientIcon} alt="insufficient token" />. </span>
            <span>You can read more about it in our <Link to="/#faqs" target="_blank"><u>FAQs</u></Link>.</span>
          </li>
        </ul>
      </div>
    ),
    headline: 'By connecting a Google account, you allow Trova to:',
    showFooter: true,
    connectBtns: (link:string) => (
      <Styled.SigninBtn href={link}>
        <div className="logoContainer">
          <img src={googleIcon} alt="Google"/>
        </div>
        <span className="text-center">Connect with Google</span>
      </Styled.SigninBtn>
    )
  },
  gdrive: {
    top: 'If you would like to see results from your Google Drive account(s) while you search, you can connect your Google account(s) to Trova. This data is only used for your search result.',
    middle: (
      <div>
        <img src={gdriveIcon} alt="Gmail Logo" />
        <span>Index your Google Drive to make them searchable for you</span>
        <ul className="token-status-container">
          <li>
            <span>Please check the box “View your email messages and settings” for Trova to search, so we won’t ask you to connect again to your account with <img src={InsufficientIcon} alt="insufficient token" />. </span>
            <span>You can read more about it in our <Link to="/#faqs" target="_blank"><u>FAQs</u></Link>.</span>
          </li>
        </ul>
      </div>
    ),
    headline: 'By connecting a Google account, you allow Trova to:',
    showFooter: true,
    connectBtns: (link:string) => (
      <Styled.SigninBtn href={link}>
        <div className="logoContainer">
          <img src={googleIcon} alt="Google"/>
        </div>
        <span className="text-center">Connect with Google</span>
      </Styled.SigninBtn>
    )
  },
  'office-365': {
    top: 'If you would like to see results from your Office365 account(s) while you search, you can connect your Office365 accounts to Trova. This data is only used for your search result.',
    headline: 'Connecting to an Office365 account allows Trova to:',
    middle: (
      <div>
        <span>Index your content to make them searchable for you</span>
        <ul className="token-status-container">
          <li>
            <span>Some organizations require an admin’s consent to permit third-party apps.</span>
            <p><img src={TeamsLimitedIcon} alt="teams limited" /> will appear when further action is required.</p>
          </li>
        </ul>
      </div>
    ),
    showFooter: true,
    connectBtns: (link:string|string[]) => (
      <Styled.StyledModalBtn href={link} className="FeedbackFooter">
        Connect
      </Styled.StyledModalBtn>
    )
  },
  teams: {
    top: 'If you would like to see results from your Teams account(s) while you search, you can connect your Teams accounts to Trova. This data is only used for your search result.',
    headline: 'Connecting to a Teams account allows Trova to:',
    middle: (
      <div>
        <span>Index your content to make them searchable for you</span>
        <ul className="token-status-container">
          <li>
            <span>Some organizations require an admin’s consent to permit third-party apps.</span>
            <p><img src={TeamsLimitedIcon} alt="teams limited" /> will appear when further action is required.</p>
          </li>
        </ul>
      </div>
    ),
    connectBtns: (link:string | string[]) => {
      if (!link) {
        return null;
      }
      if (isArray(link)) {
        return (
          <>
            <Styled.StyledModalBtn href={link[0]} className="FeedbackFooter">
              Connect
            </Styled.StyledModalBtn>
          </>
        )
      }
      return (
        <Styled.StyledModalBtn href={link} className="FeedbackFooter">
          Connect
        </Styled.StyledModalBtn>
      )
    },
    showFooter: true,
  },
  general: {
    top: '',
    headline: 'Connecting this account will give Trova permission to:',
    middle: (
      <div>
        <span>Index your content to make them searchable for you</span>
        <ul className="general-list">
          <li>
            Index your content to make them searchable for you
          </li>
          <li>
            Search over the content of the account
          </li>
          <li>
            Display the content of the account to you (and only you) when you perform a search
          </li>

        </ul>
        <div>
          We won't share the content of this account with anyone. This account can be disconnected at any time, and all information associated with the account will be deleted.
          More detail regarding your account privacy can be found in our <Link to="/security" target="_blank"><u>Security practices</u></Link>.
        </div>
        <div className="token-status-container" style={{paddingLeft: 0}}>
          <span>Changes to the connected account may prompt you to reconnect the account to Trova. To notify you, we will display </span>
          <span> <img src={RevokedIcon} alt="revoked token" />, <img src={InsufficientIcon} alt="insufficient icon" />. You can read more about it in our <Link to="/#faqs" target="_blank"><u>FAQs</u></Link>.</span>
        </div>
      </div>
    ),
    showFooter: false,
    connectBtns: (link:string) => {
      return (
        <Styled.StyledModalBtn href={link} className="FeedbackFooter">
          Connect
        </Styled.StyledModalBtn>
      )
    }
  },
}

const ConnectNotice = ({ isOpen, link, type, onClose }:GoogleNoticeProps) => {
  const [contentType, setContentType] = useState<contentTypeProps>({top: '', middle: '', headline: '', showFooter: true, connectBtns: () => null});

  useEffect(() => {
    if (!type) {
      return;
    }
    if (texts[type]) {
      setContentType(texts[type]);
    } else {
      setContentType(texts.general);
    }
  }, [type]);
  if (!type || !contentType) {
    return null;
  }
  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      animation={false}
      centered
      size='lg'
      contentClassName='ModalContent'
      dialogClassName='ModalDialog'
      >
      <Styled.ModalBody>
        <Styled.ModalClose onClick={onClose} className='HeaderBtn'>
          <Styled.StyledImg src={closeIcon} alt='Close' />
        </Styled.ModalClose>
        <Styled.H3>
          Connecting an Account
        </Styled.H3>
        <Styled.Content>
          {contentType.top}
        </Styled.Content>
        <Styled.Content className="strong">
          {contentType.headline}
        </Styled.Content>
        <Styled.Content>
          {contentType.middle}
        </Styled.Content>
        {contentType.showFooter && (
          <Styled.Content>
            <div>
              You may disconnect an account or delete a Trova account at any time, and any data associated with the account(s) will be removed from our servers. Users can also easily reconnect accounts or re-sign up with Trova. No other third parties can access your data.  You can read more in our <Link to="/security" target="_blank"><u>security practices</u></Link>.
            </div>
          </Styled.Content>
        )}
      </Styled.ModalBody>
      <Styled.ModalFooter>
        {link ? contentType.connectBtns(link) : ''}
      </Styled.ModalFooter>
    </Modal>
  )
}

export default ConnectNotice;